import { isEmpty } from 'lodash';
import { MessagePlugin } from 'tdesign-react';

import { AuditStatusType, PayAuditStatusType } from '@/const/status';
import {
  IContractDetail,
  IContractDraftInfo,
  IStoryPayInfo,
  ISupplementInfo,
} from '@/interface/finance';
import { transformObjectDeep } from '@/utils/tans/object';

import { apiPathServer } from './api-config';
import { CgiResp } from './cgi';
import { API_PREFIX, API_PREFIX_FINANCE, request } from './request';

/**
 * 查询签约资格
 */
export const signCheck = async () => {
  const res = await request<CgiResp<{ check_res: boolean }>>(
    `${API_PREFIX_FINANCE}${apiPathServer.SignCheck}`,
    { method: 'POST', body: {} },
  );
  return res?.data?.check_res || false;
};

/**
 * 签约作者邀请拉新
 * @param id
 * @returns
 */
export const inviteBySignAuthor = async (vuid: string, name: string) => {
  const res = await request<CgiResp>(`${API_PREFIX_FINANCE}${apiPathServer.InvitedBySignAuthor}`, {
    method: 'POST',
    body: {
      inviter_user_id: vuid,
      login_user_name: name,
    },
  });
  return transformObjectDeep(res, 'camel') as CgiResp;
};

/**
 * 拉取签约邀请信息
 */
export const getInvitedList = async (page = 1, size = 10) => {
  const res = await request<CgiResp>(`${API_PREFIX_FINANCE}${apiPathServer.InvitedRecordList}`, {
    query: {
      page,
      size,
    },
  });
  return transformObjectDeep(res, 'camel') as CgiResp;
};

export enum SettleRuleType {
  RULE_TYPE_UNKONWN = 0,
  RULE_TYPE_PHASE = 1,
  RULE_TYPE_FINAL = 2,
  RULE_TYPE_OVER_S = 3,
  RULE_TYPE_OVER_A = 4,
  RULE_TYPE_OVER_B = 5,
  RULE_TYPE_PULL_NEW = 6,
  RULE_TYPE_BEST_WORK = 7,
  RULE_TYPE_BEST_ORIGINALITY = 8,
  RULE_TYPE_BEST_POTENTIAL = 9,
  RULE_TYPE_RANK_TEN = 10,
  RULE_TYPE_RANK_TWENTY = 11,
  RULE_TYPE_RANK_THIRTY = 12,
}

export interface ICreateSettlementRequest {
  contractId: string;
  settleRules: SettleRuleType[];
  wordUpLimitK: number;
  settleTime: number;
  finalWordCountK: number;
  pullNewCount: number;
  rankPriceTen: number;
  rankPriceTwenty: number;
  rankPriceThirty: number;
  bestWorkPrice: number;
  bestOriginalityPrice: number;
  bestPotentialPrice: number;
}

/**
 * 创建结算
 */
export const createSettlement = async (req: ICreateSettlementRequest) => {
  const res = await request<CgiResp<{ rule_id: string }>>(
    `${API_PREFIX_FINANCE}${apiPathServer.CreateSettlement}`,
    {
      method: 'POST',
      body: transformObjectDeep(
        {
          ...req,
          settleRules: req.settleRules.map(item => Number(item)),
        },
        'snake',
      ),
    },
  );
  if (res.code !== 0 || isEmpty(res?.data?.rule_id)) {
    throw new Error(res.msg || res.message);
  }
};

/**
 * 驳回合同
 */
export const rejectContract = async (id: string) => {
  const res = await request(
    `${API_PREFIX_FINANCE}${apiPathServer.RejectContract}`,
    {
      method: 'POST',
      body: {
        contract_id: id,
      },
    },
  );
  if (res.code !== 0) {
    throw new Error(res.msg || res.message);
  }
};

/**
 * 提交合同
 */
export const submitContract = async (id: string) => {
  const res = await request(
    `${API_PREFIX_FINANCE}${apiPathServer.SubmitContract}`,
    {
      method: 'POST',
      body: {
        contract_id: id,
      },
    },
  );
  if (res.code !== 0) {
    throw new Error(res.msg || res.message);
  }
};

/**
 * 下载合同
 */
export const downloadContract = async (id: string) => {
  const res = await request<CgiResp<{ url: string }>>(
    `${API_PREFIX_FINANCE}${apiPathServer.DownloadContract}`,
    {
      method: 'POST',
      body: {
        contract_id: id,
      },
    },
  );
  if (res.code !== 0) {
    throw new Error(res.msg || res.message);
  }
  return res.data.url;
};

interface ICreateContractDraftRequest {
  userItem: IContractDraftInfo;
}

/**
 * 存储乙方信息
 * @param req
 * @returns
 */
export const createContractDraft = async (req: ICreateContractDraftRequest) => {
  const res = await request<CgiResp<{ contract_id: string }>>(
    `${API_PREFIX_FINANCE}${apiPathServer.CreateContactDraft}`,
    {
      method: 'POST',
      body: transformObjectDeep(req, 'snake'),
    },
  );
  if (res.code !== 0) {
    MessagePlugin.error('发起签约失败，请稍后再试');
  }
  return res?.data?.contract_id;
};

interface ICreateContractOfficialRequest {
  contractId: string;
  supplementItem: ISupplementInfo;
}

/**
 * 存储甲方信息
 * @param req
 * @returns
 */
export const createContractOfficial = async (req: ICreateContractOfficialRequest) => {
  const res = await request<CgiResp<{ contractId: string }>>(
    `${API_PREFIX_FINANCE}${apiPathServer.CreateContactOfficial}`,
    {
      method: 'POST',
      body: transformObjectDeep(req, 'snake'),
    },
  );
  if (res.code !== 0) {
    throw new Error(res.msg || res.message);
  }
};

export interface IContractList {
  contractDetailList: IContractDetail[];
  total: number;
}

/**
 * 拉取签约列表
 */
export const getContractList = async ({
  page = 1,
  size = 10,
  workName = '',
  status = [],
}: {
  page?: number;
  size?: number;
  workName?: string;
  status?: AuditStatusType[];
}) => {
  const res = await request<CgiResp<IContractList>>(
    `${API_PREFIX_FINANCE}${apiPathServer.ContractList}`,
    {
      method: 'POST',
      body: transformObjectDeep(
        {
          page,
          size,
          workName,
          status: status?.map(e => Number(e)) || [],
        },
        'snake',
      ),
    },
  );
  const parsedRes: IContractList = transformObjectDeep(res.data || {}, 'camel');
  return {
    list: parsedRes.contractDetailList || [],
    total: parsedRes.total || 0,
  };
};

/**
 * 拉取银行列表
 */
export const getBankList = async () => {
  const res = await request<CgiResp<{ bank_list: { bank_name: string }[] }>>(`${API_PREFIX_FINANCE}${apiPathServer.BankList}`, {
    method: 'POST',
    body: {},
  });
  const parsedRes = (res?.data?.bank_list || []).map(({ bank_name }) => bank_name);
  return parsedRes;
};

export interface IProvice {
  provinceName: string;
  cityList: { cityName: string }[]
}

/**
 * 拉取省市列表
 */
export const getCityList = async () => {
  const res = await request<CgiResp<{ province_list: IProvice[] }>>(`${API_PREFIX_FINANCE}${apiPathServer.CityList}`, {
    method: 'POST',
    body: {},
  });
  const res1 = (transformObjectDeep(res?.data, 'camel')) as { provinceList: IProvice[] };
  const parsedRes = (res1.provinceList || [])
    .map(item => ({ ...item, cityList: item.cityList.map(({ cityName }) => cityName) }));
  return parsedRes;
};

/**
 * 查询付费信息
 */
export const getPayInfo = async (storyId: string) => {
  const res = await request(
    `${API_PREFIX}${apiPathServer.GetPayInfo}`,
    {
      method: 'POST',
      body: { story_id: storyId },
    },
  );
  return transformObjectDeep(res, 'camel') as CgiResp;
};

/**
 * 更新付费信息
 */
export const updatePayInfo = async (storyPayInfo: IStoryPayInfo) => {
  const res = await request(
    `${API_PREFIX}${apiPathServer.UpdatePayInfo}`,
    {
      method: 'POST',
      body: transformObjectDeep(storyPayInfo, 'snake'),
    },
  );
  return res;
};

/**
 * 提交付费信息
 */
export const submitPayAudit = async (storyId: string, storyPayInfo: IStoryPayInfo) => {
  const res = await request(
    `${API_PREFIX}${apiPathServer.SubmitPayAudit}`,
    {
      method: 'POST',
      body: transformObjectDeep(
        {
          story_id: storyId,
          upsert_pay_info: storyPayInfo,
        },
        'snake',
      ),
    },
  );
  return res;
};

/**
 * 拉取付费信息审核历史
 */
export const getPayAuditList = async ({
  storyId,
  page = 1,
  size = 10,
}: {
  storyId: string;
  page?: number;
  size?: number;
}) => {
  const res = await request(`${API_PREFIX}${apiPathServer.GetPayAuditList}`, {
    method: 'POST',
    body: {
      story_id: storyId,
      page,
      size,
    },
  });
  return transformObjectDeep(res, 'camel') as CgiResp;
};

/**
 * 提交付费信息
 */
export const auditPayInfo = async (storyId: string, status: PayAuditStatusType) => {
  const res = await request(
    `${API_PREFIX}${apiPathServer.AuditPayInfo}`,
    {
      method: 'POST',
      body: { story_id: storyId, pay_audit_status: status },
    },
  );
  return res;
};

/**
 * 仅上架价格
 */
export const publishPayInfo = async (storyId: string) => {
  const res = await request(`${API_PREFIX}${apiPathServer.PublishPayInfo}`, {
    method: 'POST',
    body: { story_id: storyId },
  });
  return res;
};

/**
 * 查询线上外显价格
 */
export const getOnlinePayInfo = async (storyId: string) => {
  const res = await request(
    `${API_PREFIX}${apiPathServer.GetOnlinePay}/release`,
    {
      method: 'POST',
      body: { story_id: storyId },
    },
  );
  return transformObjectDeep(res, 'camel') as CgiResp;
};

/**
 * 拉取签约历史列表
 */

export interface IContractHistoryList {
  settlementList: IContractHistoryDetail[];
  total: number;
}

export interface IContractHistoryDetail extends ICreateSettlementRequest {
  createdTime: number;
}

export const getContractHistoryList = async ({
  page = 1,
  size = 10,
  contractId = '',
}: {
  page?: number;
  size?: number;
  contractId?: string;
}) => {
  const res = await request<CgiResp<IContractHistoryList>>(
    `${API_PREFIX_FINANCE}${apiPathServer.ContractHistoryList}`,
    {
      method: 'POST',
      body: transformObjectDeep(
        {
          page,
          size,
          contractId,
        },
        'snake',
      ),
    },
  );
  const parsedRes: IContractHistoryList = transformObjectDeep(res.data || {}, 'camel');
  return {
    list: parsedRes.settlementList || [],
    total: parsedRes.total || 0,
    code: res?.code,
  };
};
